import { memo, useState, useEffect, useRef } from 'react';
import * as styles from './styles';
import * as variants from './animation';
import { buttonEventHandler } from 'lib/utils/button-event-handler';
import Button from 'components/global/Button/Primary';

type StickyBarProps = {
  text: string;
  href?: string;
  onCtaClick?: () => void;
  imageSrc?: string;
  ctaLabel?: string;
  ctaDisabled?: boolean;
  buttonEventName?: string;
};

const StickyBar = ({
  text,
  href,
  onCtaClick,
  imageSrc,
  ctaLabel = 'Start Now',
  ctaDisabled = false,
  buttonEventName,
}: StickyBarProps) => {
  const [animateState, setAnimateState] = useState<
    'hidden' | 'visible' | 'exiting'
  >('hidden');
  const prevVisibleRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const [imageHiddenX, setImageHiddenX] = useState(500);
  const [circleSize, setCircleSize] = useState(64);

  useEffect(() => {
    const measure = () => {
      if (!wrapperRef.current || !rowRef.current) return;
      const barRect = wrapperRef.current.getBoundingClientRect();
      const rowRect = rowRef.current.getBoundingClientRect();
      const barWidth = barRect.width;
      const isMobile = barWidth < 480;
      const imgWidth = isMobile ? 36 : 48;
      const cs = isMobile ? 52 : 64;
      const rowLeft = rowRect.left - barRect.left;
      setImageHiddenX(barWidth - cs / 2 - rowLeft - imgWidth / 2);
      setCircleSize(cs);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapperRef.current) ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const htmlEl = document.documentElement;
    const update = () => {
      const visible = htmlEl.classList.contains('with-scrolled');
      if (visible) {
        setAnimateState('visible');
      } else if (prevVisibleRef.current) {
        setAnimateState('exiting');
      }
      prevVisibleRef.current = visible;
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(htmlEl, { attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const ctaContent =
    onCtaClick && !ctaDisabled ? (
      <Button
        size={{ base: 'sm', tablet: 'md', desktop: 'md' }}
        onClick={() => {
          if (buttonEventName)
            buttonEventHandler(buttonEventName, 'PDP sticky nav');
          onCtaClick();
        }}
        light
      >
        {ctaLabel}
      </Button>
    ) : href && !ctaDisabled ? (
      <styles.InlineLinkStyled href={href} prefetch={false}>
        <Button
          size={{ base: 'sm', tablet: 'md', desktop: 'md' }}
          onClick={() =>
            buttonEventName &&
            buttonEventHandler(buttonEventName, 'PDP sticky nav')
          }
          light
        >
          {ctaLabel}
        </Button>
      </styles.InlineLinkStyled>
    ) : (
      <Button
        size={{ base: 'sm', tablet: 'md', desktop: 'md' }}
        light
        disabled={ctaDisabled}
      >
        {ctaLabel}
      </Button>
    );

  return (
    <styles.Wrapper ref={wrapperRef}>
      <styles.MotionPillBackground
        animate={animateState}
        variants={variants.background}
        initial='hidden'
        custom={circleSize}
        aria-hidden='true'
        onAnimationComplete={(definition) => {
          if (definition === 'exiting') setAnimateState('hidden');
        }}
      />
      <styles.DynamicElementsRow ref={rowRef}>
        <styles.ProductImage
          src={imageSrc}
          alt={text}
          animate={animateState}
          variants={variants.image}
          initial='hidden'
          custom={imageHiddenX}
        />
        <styles.TitleMask>
          <styles.TextContainer
            animate={animateState}
            variants={variants.textContainer}
            initial='hidden'
            custom={text.split(' ').length}
          >
            {text.split(' ').map((word, i) => (
              <styles.WordMask key={i}>
                <styles.Word variants={variants.textWord}>{word}</styles.Word>
              </styles.WordMask>
            ))}
          </styles.TextContainer>
        </styles.TitleMask>
        <styles.CTAWrapper
          animate={animateState}
          variants={variants.cta}
          initial='hidden'
        >
          {ctaContent}
        </styles.CTAWrapper>
      </styles.DynamicElementsRow>
    </styles.Wrapper>
  );
};

export default memo(StickyBar);
